import { __awaiter, __generator, __read } from "tslib";
import { CustomFilterType, getFilteredTabId, useSingletonEdgeworthCustomPivot, useSingletonEdgeworthPivot, } from '@office-start/base-edgeworth-control';
import { useCapabilities } from '@office-start/capabilities-service';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { showConfirmationDialog, } from '@office-start/confirmation-dialog-component';
import { useSharedContext } from '@office-start/context-service';
import { DAY_IN_MS } from '@office-start/date-utility';
import { copyToClipboard } from '@office-start/dom-utility';
import { paintEwFirstMarker, } from '@office-start/edgeworth-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { getFileClickData, getFileTypeNameFromType, getOtelReferralFromDocument, getOtelReferralFromFileLocation, getReferralFromDocument, getReferralFromFileLocation, } from '@office-start/file-utility';
import { fetchPersonPictureData, getUrlFileInfo, } from '@office-start/graph-service';
import { logClickEvent, logGenericEvent, } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { getLaunchInstrumentationParamsFromKnownArea, launchContent, launchFolder, launchUrl, } from '@office-start/launcher-service';
import { OfficeRouteItemType, } from '@office-start/officehome-types';
import { logFeature, logReferUserAction, logUserAction, } from '@office-start/telemetry-service';
import { useToastActionCreators } from '@office-start/toast-utility';
import { useCallback, useMemo, useRef } from 'react';
import { logTagAction } from '../utilities/logTagAction';
import { populateClickParamsWithFilters } from '../utilities/populateClickParamsWithFilters';
import { useEdgeworthDocumentActionsCreator } from './useEdgeworthDocumentActionsCreator';
var logOpenCloseResult = function (_a) {
    var _b;
    var area = _a.area, id = _a.id, isOpen = _a.isOpen, contentActionsEnabled = _a.contentActionsEnabled, document = _a.document;
    var result = isOpen ? 'Open' : 'Close';
    var documentType = (document === null || document === void 0 ? void 0 : document.substrateInfo) !== undefined
        ? document === null || document === void 0 ? void 0 : document.substrateInfo.contentDomain
        : getFileTypeNameFromType(document === null || document === void 0 ? void 0 : document.fileType);
    logClickEvent(id, result, area, undefined /* referral - Do not log a referral since this is a UX event */, undefined /* referralPlatform */, (_b = {},
        _b["DocumentType" /* InstrumentationParam.DocumentType */] = documentType,
        _b));
    logUserAction({
        id: id,
        area: area,
        result: result,
        target: contentActionsEnabled ? 'ContentActionsMenu' : 'ActionsMenu',
    });
};
export function useEdgeworthCallbacks(_a) {
    var _this = this;
    var _b, _c;
    var resync = _a.resync, queryTrie = _a.queryTrie, pinMutation = _a.pinMutation, hideMutation = _a.hideMutation, instrumentationArea = _a.instrumentationArea, locStrings = _a.locStrings, tags = _a.tags;
    var featureGates = useFeatureGates().data;
    var sharedContext = useSharedContext().data;
    var officeComponentContext = useOfficeComponentContext().data;
    var capabilities = useCapabilities().data;
    var graphXhrOptions = useMemo(function () {
        return sharedContext && officeComponentContext
            ? {
                graphApiRoot: sharedContext.graphApiRoot,
                correlationId: officeComponentContext.correlationId,
                getAccessToken: officeComponentContext.getAccessToken,
                jwtAuthEnabled: officeComponentContext.jwtAuthEnabled,
                officeApiRoot: officeComponentContext.officeHomeApiRoot,
            }
            : undefined;
    }, [officeComponentContext, sharedContext]);
    var clickCountRef = useRef(0);
    var isTextFilterUsed = useRef(false);
    var currentTextFilter = useRef();
    var _d = __read(pinMutation, 1), togglePinnedState = _d[0];
    var _f = __read(hideMutation, 1), toggleHiddenState = _f[0];
    var renderTimingInstrumentationParams = useMemo(function () { return ({
        paintMarker: paintEwFirstMarker,
        clickMarker: 'Edgeworth_First_ItemClick',
    }); }, []);
    var getCommonActionInstrumentation = useCallback(function (item, index) {
        var _a;
        var _b, _c;
        // InstrumentationParams
        var documentType = item.substrateInfo !== undefined
            ? item.substrateInfo.contentDomain
            : getFileTypeNameFromType(item.fileType);
        var instrumentationParams = (_a = {},
            _a["Index" /* InstrumentationParam.Index */] = index.toString(),
            _a["DocumentType" /* InstrumentationParam.DocumentType */] = documentType,
            _a["ItemCategory" /* InstrumentationParam.ItemCategory */] = 'edgeworth-list',
            _a["Pinned" /* InstrumentationParam.Pinned */] = ((_b = item.isPinned) === null || _b === void 0 ? void 0 : _b.toString()) || '',
            _a["ActivityCollaboration" /* InstrumentationParam.ActivityCollaboration */] = ((_c = item.isCollab) === null || _c === void 0 ? void 0 : _c.toString()) || '',
            _a);
        return {
            instrumentationArea: instrumentationArea,
            instrumentationParams: instrumentationParams,
        };
    }, [instrumentationArea]);
    var instrumentAction = useCallback(function (actionId, item, index) {
        var _a = getCommonActionInstrumentation(item, index), instrumentationParams = _a.instrumentationParams, instrumentationArea = _a.instrumentationArea;
        var referral;
        var referralPlatform;
        var otelReferral;
        var isScdType = false;
        if (actionId === 'open' || actionId === 'openInApp') {
            referral = getReferralFromDocument(item);
            referralPlatform = actionId === 'open' ? 'Web' : 'Native';
            var _b = getOtelReferralFromDocument(item), otelReferralApp = _b.otelReferral, scdFlag = _b.isScdType;
            otelReferral = otelReferralApp;
            isScdType = scdFlag;
        }
        else if (actionId === 'openFileLocation') {
            referral = item.webUrl
                ? getReferralFromFileLocation(item.webUrl)
                : undefined;
            referralPlatform = item.isLocalFile ? 'Native' : 'Web';
            if (item.webUrl) {
                otelReferral = getOtelReferralFromFileLocation(item.webUrl);
            }
        }
        logClickEvent(KnownInstrumentationEvents.ActionsMenuItemClick, actionId, instrumentationArea, referral, referralPlatform, instrumentationParams);
        // TODO: Remove above Aria Click logging once OTEL migration complete
        if (instrumentationArea) {
            if (otelReferral === undefined) {
                logUserAction({
                    eventName: KnownInstrumentationEvents.ActionsMenuItemClick,
                    id: actionId,
                    area: instrumentationArea,
                    result: 'Navigate',
                    target: 'ActionsMenuItem',
                });
            }
            else {
                logReferUserAction(actionId, instrumentationArea, otelReferral, undefined, // SubArea
                undefined, // ActionType
                KnownInstrumentationEvents.ActionsMenuItemClick, isScdType);
            }
        }
    }, [getCommonActionInstrumentation]);
    var navigateToMyContent = useCallback(function (instrumentationArea, elementId) {
        var route = {
            route: 'MyContent',
            type: OfficeRouteItemType.Base,
        };
        var param = getLaunchInstrumentationParamsFromKnownArea(instrumentationArea, undefined, elementId);
        var action = 'OpenDocument';
        launchUrl(route, { action: action }, param);
    }, []);
    var _g = useSingletonEdgeworthCustomPivot(), setStickyCustomPivot = _g.setStickyCustomPivot, setCurrentCustomPivot = _g.setCurrentCustomPivot;
    var onTagItemClick = useCallback(function (tagItem, _document) {
        var customPivot = {
            customFilterType: CustomFilterType.Tagv2,
            customFilterKey: tagItem.uniqueId,
            customFilterTitle: tagItem.value,
        };
        // When we're in MyContent, clicking the tag item should just update the current custom pivot
        if (instrumentationArea === 'MyContent') {
            setCurrentCustomPivot(customPivot);
            logTagAction('MyContent', 'TagItemClick', 'Navigate');
        }
        else {
            setStickyCustomPivot(customPivot);
            navigateToMyContent(instrumentationArea, 'tag_item_click');
        }
    }, [
        instrumentationArea,
        navigateToMyContent,
        setCurrentCustomPivot,
        setStickyCustomPivot,
    ]);
    var createDocumentActions = useEdgeworthDocumentActionsCreator({
        togglePinnedState: togglePinnedState,
        toggleHiddenState: toggleHiddenState,
        instrumentAction: instrumentAction,
        getCommonActionInstrumentation: getCommonActionInstrumentation,
        locStrings: locStrings,
        tags: tags,
        navigateToMyContent: navigateToMyContent,
    });
    var onActionMenuToggled = useCallback(function (isOpen, document) {
        logOpenCloseResult({
            area: instrumentationArea,
            id: (capabilities === null || capabilities === void 0 ? void 0 : capabilities.contentActionsEnabled)
                ? KnownInstrumentationEvents.ContentActionsMenuToggle
                : KnownInstrumentationEvents.ActionsMenuToggle,
            isOpen: isOpen,
            contentActionsEnabled: capabilities === null || capabilities === void 0 ? void 0 : capabilities.contentActionsEnabled,
            document: document,
        });
    }, [capabilities === null || capabilities === void 0 ? void 0 : capabilities.contentActionsEnabled, instrumentationArea]);
    var onCommandMenuToggled = useCallback(function (isOpen, document) {
        logOpenCloseResult({
            area: instrumentationArea,
            id: KnownInstrumentationEvents.ContentActionsCommandMenuToggle,
            isOpen: isOpen,
            contentActionsEnabled: capabilities === null || capabilities === void 0 ? void 0 : capabilities.contentActionsEnabled,
            document: document,
        });
    }, [capabilities === null || capabilities === void 0 ? void 0 : capabilities.contentActionsEnabled, instrumentationArea]);
    var createErrorNotification = useToastActionCreators().createErrorNotification;
    var onDocumentClick = useCallback(function (item, index, event, activeFilters, isGridView) { return __awaiter(_this, void 0, void 0, function () {
        var eventName, clickData, launchInstrumentationParams, fileInfo, parsedUrl, _e_1, contentLaunchParams;
        var _a, _b, _c, _d, _f, _g;
        return __generator(this, function (_h) {
            switch (_h.label) {
                case 0:
                    if (item.isDeleted) {
                        event === null || event === void 0 ? void 0 : event.preventDefault();
                        return [2 /*return*/];
                    }
                    if (event === null || event === void 0 ? void 0 : event.defaultPrevented) {
                        return [2 /*return*/];
                    }
                    event === null || event === void 0 ? void 0 : event.preventDefault();
                    eventName = KnownInstrumentationEvents.EdgeworthClick;
                    clickData = getFileClickData(item, eventName, index, 'Click', instrumentationArea);
                    clickData["ItemId" /* InstrumentationParam.ItemId */] =
                        ((_b = (_a = event === null || event === void 0 ? void 0 : event.currentTarget) === null || _a === void 0 ? void 0 : _a.getAttribute) === null || _b === void 0 ? void 0 : _b.call(_a, 'clickId')) || 'item';
                    // TODO: Figure out how to get index relative to view
                    //clickData[InstrumentationParam.RelativeIndex] = index.toString();
                    clickData["ItemCategory" /* InstrumentationParam.ItemCategory */] = 'edgeworth-list';
                    clickData["ResourceId" /* InstrumentationParam.ResourceId */] =
                        ((_c = item.sharepointIds) === null || _c === void 0 ? void 0 : _c.listItemUniqueId) || '';
                    clickData["ActivityCollaboration" /* InstrumentationParam.ActivityCollaboration */] =
                        ((_d = item.isCollab) === null || _d === void 0 ? void 0 : _d.toString()) || '';
                    populateClickParamsWithFilters(clickData, activeFilters);
                    clickData["HasTextFilter" /* InstrumentationParam.HasTextFilter */] =
                        (!!currentTextFilter.current).toString();
                    clickData["Layout" /* InstrumentationParam.Layout */] = isGridView
                        ? 'Grid'
                        : 'List';
                    launchInstrumentationParams = {
                        area: instrumentationArea,
                        clickParams: clickData,
                        eventName: eventName,
                        elementId: eventName,
                        renderTimingInstrumentationParams: clickCountRef.current == 0
                            ? renderTimingInstrumentationParams
                            : undefined,
                        referral: getReferralFromDocument(item),
                    };
                    if (!(graphXhrOptions &&
                        ((_g = (_f = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _f === void 0 ? void 0 : _f.edgeworthConfigGates) === null || _g === void 0 ? void 0 : _g.edgeworthConfigLinksEnabled) &&
                        item.fileType === 'Link' &&
                        item.driveId &&
                        item.docId)) return [3 /*break*/, 5];
                    _h.label = 1;
                case 1:
                    _h.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, getUrlFileInfo(item.driveId, item.docId, graphXhrOptions)];
                case 2:
                    fileInfo = _h.sent();
                    if (fileInfo) {
                        parsedUrl = new URL(fileInfo.address);
                        if (!parsedUrl ||
                            (parsedUrl.protocol !== 'http:' &&
                                parsedUrl.protocol !== 'https:')) {
                            throw new Error();
                        }
                        launchUrl(parsedUrl.toString(), undefined, launchInstrumentationParams);
                    }
                    return [3 /*break*/, 4];
                case 3:
                    _e_1 = _h.sent();
                    logFeature({
                        featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
                        eventName: 'OpenLink',
                        params: {
                            Error: 'GenericError',
                        },
                    });
                    if (locStrings) {
                        createErrorNotification(locStrings.linksResources.linkOpenFailureMessage);
                    }
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
                case 5:
                    contentLaunchParams = {
                        action: 'OpenDocument',
                        document: item,
                        event: event,
                        useWebUrl: true,
                        wdOriginType: item.fileType === 'Stream'
                            ? item.isPinned
                                ? "EDGEWORTH-FAVORITES" /* WdOriginType.EdgeworthFavorites */
                                : "RecentVideo.View" /* WdOriginType.RecentVideoView */
                            : "EDGEWORTH" /* WdOriginType.Edgeworth */,
                    };
                    launchContent(contentLaunchParams, launchInstrumentationParams);
                    clickCountRef.current = clickCountRef.current + 1;
                    return [2 /*return*/];
            }
        });
    }); }, [
        createErrorNotification,
        (_c = (_b = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _b === void 0 ? void 0 : _b.edgeworthConfigGates) === null || _c === void 0 ? void 0 : _c.edgeworthConfigLinksEnabled,
        graphXhrOptions,
        instrumentationArea,
        locStrings,
        renderTimingInstrumentationParams,
    ]);
    var onMeetingArtifactClick = useCallback(function (item, index, event) {
        if (event === null || event === void 0 ? void 0 : event.defaultPrevented) {
            return;
        }
        event === null || event === void 0 ? void 0 : event.preventDefault();
        var eventName = KnownInstrumentationEvents.EdgeworthClick;
        var clickData = getFileClickData(item, eventName, index, 'Click', instrumentationArea);
        // TODO: Figure out how to get index relative to view
        //clickData[InstrumentationParam.RelativeIndex] = index.toString();
        clickData["ItemCategory" /* InstrumentationParam.ItemCategory */] = 'edgeworth-list';
        clickData["ItemId" /* InstrumentationParam.ItemId */] = 'meetingartifact';
        if (item.createdDate) {
            var timeElapsedSinceMeeting = Date.now() - item.createdDate.valueOf();
            var daysElapsed = Math.floor(timeElapsedSinceMeeting / DAY_IN_MS);
            clickData["TimeElapsed" /* InstrumentationParam.TimeElapsed */] =
                daysElapsed.toString();
        }
        var launchInstrumentationParams = {
            area: instrumentationArea,
            elementId: eventName,
            eventName: eventName,
            referral: getReferralFromDocument(item),
            renderTimingInstrumentationParams: clickCountRef.current == 0
                ? renderTimingInstrumentationParams
                : undefined,
            clickParams: clickData,
        };
        var contentLaunchParams = {
            action: 'OpenDocument',
            document: item,
            event: event,
            useWebUrl: true,
            wdOriginType: "EDGEWORTH" /* WdOriginType.Edgeworth */,
        };
        launchContent(contentLaunchParams, launchInstrumentationParams);
        clickCountRef.current = clickCountRef.current + 1;
    }, [instrumentationArea, renderTimingInstrumentationParams]);
    var onSelectFilter = useCallback(function (key, isSelected) {
        var _a;
        var params = (_a = {},
            _a['ItemId'] = key,
            _a['State'] = isSelected ? 'Selected' : 'Deselected',
            _a);
        logGenericEvent(KnownInstrumentationEvents.EdgeworthFilterSelect, params);
        //TODO: Remove above Aria logging after fully migrating to OTEL
        logUserAction({
            id: 'EdgeworthFilterSelect',
            area: 'Edgeworth',
            result: 'Toggle',
            target: 'Edgeworth',
            eventName: 'EdgeworthFilterSelect',
        });
        logFeature({
            featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
            eventName: 'EdgeworthFilterSelect',
            params: params,
            area: 'Edgeworth',
        });
    }, []);
    var onSelectTab = useCallback(function (tabEvent) {
        // Filter out tab ID to avoid instrumenting PIIs
        var tabId = getFilteredTabId(tabEvent.tabId);
        if (tabId) {
            logClickEvent(KnownInstrumentationEvents.EdgeworthPivotClick, tabId, instrumentationArea);
            logUserAction({
                eventName: KnownInstrumentationEvents.EdgeworthPivotClick,
                id: tabId,
                area: instrumentationArea,
                result: 'Select',
                target: 'EdgeworthPivot',
            });
        }
    }, [instrumentationArea]);
    var getActivityPersonUrl = useCallback(function (item) { return __awaiter(_this, void 0, void 0, function () {
        var upn, personPictureData, e_1;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 3, , 4]);
                    upn = (_b = (_a = item.activity) === null || _a === void 0 ? void 0 : _a.people[0]) === null || _b === void 0 ? void 0 : _b.upn;
                    if (!upn) return [3 /*break*/, 2];
                    return [4 /*yield*/, fetchPersonPictureData(upn)];
                case 1:
                    personPictureData = (_c.sent()).result;
                    return [2 /*return*/, personPictureData];
                case 2: return [3 /*break*/, 4];
                case 3:
                    e_1 = _c.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, []);
    var onTextFilterUsed = useCallback(function (text) {
        currentTextFilter.current = text;
        if (text && !isTextFilterUsed.current) {
            logGenericEvent(KnownInstrumentationEvents.EdgeworthTextFilterUsed);
            //TODO: Remove above Aria logging after fully migrating to OTEL
            logUserAction({
                id: 'EdgeworthTextFilterUsed',
                area: 'Edgeworth',
                result: 'Filter',
                target: 'Edgeworth',
            });
            isTextFilterUsed.current = true;
        }
    }, []);
    var onPathClick = useCallback(function (item, index, event) {
        if (event) {
            event.preventDefault();
        }
        if (item.isLocalFile) {
            var copyToClipboardIfPossible_1 = function () {
                if (item.webUrl) {
                    copyToClipboard(item.webUrl);
                }
            };
            if (item.attributionUrl) {
                launchFolder(item.attributionUrl).catch(function () {
                    return copyToClipboardIfPossible_1();
                });
            }
            else {
                copyToClipboardIfPossible_1();
            }
            return;
        }
        if (!item.attributionUrl) {
            // If attributionUrl is missing, return early
            return;
        }
        var action = 'OpenFileLocation';
        launchUrl(item.attributionUrl, { action: action });
        var clickData = getFileClickData(item, KnownInstrumentationEvents.MruPathClick, index, 'Click', instrumentationArea);
        clickData["Referral" /* InstrumentationParam.Referral */] =
            getReferralFromFileLocation(item.url) || '';
        clickData["ItemCategory" /* InstrumentationParam.ItemCategory */] = 'edgeworth-list';
        clickData["ResourceId" /* InstrumentationParam.ResourceId */] = item.resourceId || '';
        // PWA TODO: What should this be for local explorer launches?
        var clickReferral = clickData["Referral" /* InstrumentationParam.Referral */];
        logClickEvent(KnownInstrumentationEvents.MruPathClick, KnownInstrumentationEvents.MruPathClick, instrumentationArea, clickReferral, item.isLocalFile ? 'Native' : 'Web', clickData);
        // TODO: Remove above Aria Click logging once OTEL migration complete
        var otelReferral = getOtelReferralFromFileLocation(item.url);
        if (otelReferral === undefined) {
            logUserAction({
                eventName: KnownInstrumentationEvents.MruPathClick,
                id: 'MruPath',
                area: instrumentationArea,
                result: 'Navigate',
                target: 'MruPath',
            });
        }
        else {
            logUserAction({
                eventName: KnownInstrumentationEvents.MruPathClick,
                id: 'MruPath',
                area: instrumentationArea,
                result: 'Refer',
                target: otelReferral,
            });
        }
    }, [instrumentationArea]);
    var onTagEdit = useCallback(function (originalTag, newTag) {
        logTagAction('MyContent', 'TagEdit', 'Edit', originalTag.color !== newTag.color ? newTag.color : undefined);
        tags === null || tags === void 0 ? void 0 : tags.patchTag(originalTag, newTag).then(function () {
            if (newTag.value && newTag.uniqueId) {
                var customPivot = {
                    customFilterType: CustomFilterType.Tagv2,
                    customFilterKey: newTag.uniqueId,
                    customFilterTitle: newTag.value,
                };
                setCurrentCustomPivot(customPivot);
            }
        });
    }, [setCurrentCustomPivot, tags]);
    var setCurrentPivot = useSingletonEdgeworthPivot().setCurrentPivot;
    var onTagDelete = useCallback(function (tag) {
        var confirmationDialog = {
            confirmationText: (locStrings === null || locStrings === void 0 ? void 0 : locStrings.edgeworthTaggingResources.deleteTagConfirmationText) || '',
            affirmationButtonTitle: (locStrings === null || locStrings === void 0 ? void 0 : locStrings.edgeworthTaggingResources.deleteTagButtonText) ||
                '',
            action: function () {
                logTagAction('MyContent', 'TagDelete', 'Delete');
                tags === null || tags === void 0 ? void 0 : tags.deleteTag(tag).then(function () {
                    setCurrentCustomPivot(undefined);
                    setCurrentPivot('all');
                });
            },
            dialogTitle: locStrings === null || locStrings === void 0 ? void 0 : locStrings.edgeworthTaggingResources.deleteTagDialogTitle,
        };
        showConfirmationDialog(confirmationDialog);
    }, [setCurrentCustomPivot, setCurrentPivot, tags, locStrings]);
    return {
        createDocumentActions: createDocumentActions,
        filterDocumentsByText: queryTrie,
        getActivityPersonUrl: getActivityPersonUrl,
        onActionMenuToggled: onActionMenuToggled,
        onCommandMenuToggled: onCommandMenuToggled,
        onDocumentClick: onDocumentClick,
        onMeetingArtifactClick: onMeetingArtifactClick,
        onPathClick: onPathClick,
        onSelectFilter: onSelectFilter,
        onSelectTab: onSelectTab,
        onSync: resync,
        onTagItemClick: onTagItemClick,
        onTextFilterUsed: onTextFilterUsed,
        onTagDelete: onTagDelete,
        onTagEdit: onTagEdit,
    };
}
//# sourceMappingURL=useEdgeworthCallbacks.js.map